'use client';

/**
 * GlobalFooter — single shared footer, three variants keyed on pathname.
 *
 *   Marketing variant (default): three-column legal + company + brand grid
 *     with trust badges. Shown on /, /flow-1, /home, /editorial, legal
 *     pages, etc.
 *
 *   Dashboard variant: one-line tight footer with Terms · Privacy ·
 *     Medical Disclaimer links inline. Shown on /dashboard/*, /upsell/*,
 *     /catalog/*.
 *
 *   Quiz variant (Track 5.Y3-start-quiz-bugfix-polish, 2026-05-12):
 *     educational/911 line ONLY — no Terms/Privacy/Medical Disclaimer
 *     links. Shown on /start/* only. Designer-lead flagged the inline
 *     legal links as misclick bait during the multi-step quiz; the full
 *     footer with all legal links remains accessible site-wide via the
 *     Marketing variant on /, /editorial, /privacy-policy, etc.
 *
 * Behavior toggled by usePathname(). Lives at the bottom of <body> via
 * src/app/layout.tsx so it renders on every user-facing route.
 */

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { usePathname } from 'next/navigation';
import { HeartPulse, Lock, ShieldCheck, type LucideIcon } from 'lucide-react';
import { LegalLink } from './LegalLink';

export default function GlobalFooter() {
  const pathname = usePathname();
  const isDashboard = pathname?.startsWith('/dashboard');
  const isFunnel = pathname?.startsWith('/upsell');
  const isQuiz = pathname?.startsWith('/start');
  // Track 5.Z5 (2026-05-12): /welcome is a post-upsell celebration
  // surface — same in-process footer profile as /dashboard/* and /upsell/*.
  const isWelcome = pathname?.startsWith('/welcome');
  // Track 5.Y-cat-catalog-browse-surface — /catalog joins the in-process
  // surface set so the marketing LEGAL/COMPANY grid doesn't leak into
  // the browse-and-add flow. Same minimal one-line variant as the others.
  const isCatalog = pathname?.startsWith('/catalog');

  // Track 5.Z3 (2026-05-12): /checkout/plan gets its own minimal sales
  // footer — same misclick-bait concern as /start, plus the dense
  // 3-column MarketingFooter visually competes with the high-value
  // reveal sales surface above it.
  const isSalesPlan = pathname?.startsWith('/checkout/plan');

  if (isQuiz) return <QuizFooter />;
  if (isSalesPlan) return <SalesFooter />;
  if (isDashboard || isFunnel || isCatalog || isWelcome) return <DashboardFooter />;
  return <MarketingFooter />;
}

/** Educational/911 line only — no inline legal links. /start only. */
function QuizFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="mx-auto max-w-7xl px-6 py-4 text-xs text-slate-500">
        Educational information only. Not medical advice. For emergencies,
        call 911.
      </div>
    </footer>
  );
}

/** Track 5.Z3 sales-surface footer for /checkout/plan. Trust pills
 *  row + educational/911 disclaimer + ONE small Terms link. Replaces
 *  the full MarketingFooter on this surface. */
function SalesFooter() {
  return (
    <footer className="border-t border-slate-200 bg-[#fdfaf5]">
      <div className="mx-auto max-w-6xl px-6 py-8 space-y-4">
        {/* Trust pills row — mirrors the hero-card row */}
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[10px] font-semibold tracking-wider text-slate-600 uppercase">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
            HSA/FSA eligible
          </span>
          <span className="text-slate-300" aria-hidden>·</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
            CLIA-certified labs
          </span>
          <span className="text-slate-300" aria-hidden>·</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
            HIPAA-aligned
          </span>
        </div>
        {/* Educational disclaimer + single Terms link */}
        <div className="flex flex-wrap items-center justify-center gap-x-3 text-xs text-slate-500 text-center">
          <span>
            Educational information only. Not medical advice. For emergencies, call 911.
          </span>
          <span className="text-slate-300" aria-hidden>·</span>
          <LegalLink slug="terms" className="hover:text-slate-800">Terms</LegalLink>
        </div>
      </div>
    </footer>
  );
}

/** Tight one-liner footer for /dashboard/* + /upsell/* + /catalog/*. */
function DashboardFooter() {
  return (
    <footer className="border-t border-slate-200 bg-white">
      <div className="mx-auto max-w-7xl px-6 py-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
        <span>
          Educational information only. Not medical advice. For emergencies,
          call 911.
        </span>
        <span className="text-slate-300">·</span>
        <LegalLink slug="terms" className="hover:text-slate-800">Terms</LegalLink>
        <span className="text-slate-300">·</span>
        <LegalLink slug="privacy-policy" className="hover:text-slate-800">Privacy</LegalLink>
        <span className="text-slate-300">·</span>
        <LegalLink slug="medical-disclaimer" className="hover:text-slate-800">Medical Disclaimer</LegalLink>
      </div>
    </footer>
  );
}

/** Full marketing footer. */
function MarketingFooter() {
  // Year read in useEffect to avoid year-boundary SSR/hydration mismatch
  // (React #418 chronicle — see docs/post-mortems/). Lint rule
  // react-hooks/set-state-in-effect flags this pattern; the alternative
  // (read Date in render) is exactly what causes the hydration mismatch
  // we're guarding against. Suppression is intentional.
  const [year, setYear] = useState<number | null>(null);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { setYear(new Date().getUTCFullYear()); }, []);

  return (
    <footer className="border-t border-white/10 bg-[#0f2534] text-white/70 text-sm">
      <div className="mx-auto max-w-7xl px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {/* Brand + trust */}
          <div>
            <Link href="/" className="inline-block text-white font-bold text-xl tracking-tight">
              feelgood.health
            </Link>
            <p className="mt-1 text-xs text-white/50">Preventive biomarker testing</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <TrustBadge icon={HeartPulse}>HSA/FSA eligible</TrustBadge>
              <TrustBadge icon={ShieldCheck}>CLIA-certified labs</TrustBadge>
              <TrustBadge icon={Lock}>HIPAA-aligned</TrustBadge>
            </div>
          </div>

          <FooterGroup title="Legal">
            <LegalFooterLink slug="terms">Terms</LegalFooterLink>
            <LegalFooterLink slug="privacy-policy">Privacy</LegalFooterLink>
            <LegalFooterLink slug="refund-policy">Refund Policy</LegalFooterLink>
            <LegalFooterLink slug="cookie-policy">Cookies</LegalFooterLink>
            <LegalFooterLink slug="medical-disclaimer">Medical Disclaimer</LegalFooterLink>
          </FooterGroup>

          <FooterGroup title="Company">
            <FooterLink href="/about">About</FooterLink>
            <FooterLink href="/contact">Contact</FooterLink>
            <FooterLink href="/report-an-error">Report a Content Error</FooterLink>
            <FooterLink href="/editorial">Editorial Policy</FooterLink>
          </FooterGroup>
        </div>

        <div className="mt-10 pt-6 border-t border-white/10 text-xs text-white/50">
          © {year ?? ''} feelgood.health. Educational information,
          not medical advice. In a medical emergency, call 911.
        </div>
      </div>
    </footer>
  );
}

function FooterGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-white text-xs font-semibold uppercase tracking-[0.2em] mb-4">
        {title}
      </h3>
      <ul className="space-y-2.5">{children}</ul>
    </div>
  );
}

function LegalFooterLink({ slug, children }: { slug: import('./LegalModal').LegalSlug; children: React.ReactNode }) {
  return (
    <li>
      <LegalLink
        slug={slug}
        className="text-white/70 hover:text-white transition-colors"
      >
        {children}
      </LegalLink>
    </li>
  );
}

function FooterLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <li>
      <Link
        href={href}
        className="text-white/70 hover:text-white transition-colors"
      >
        {children}
      </Link>
    </li>
  );
}

function TrustBadge({ icon: Icon, children }: { icon: LucideIcon; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-[10px] font-medium text-white/70 uppercase tracking-wider">
      <Icon size={12} className="text-emerald-300" />
      {children}
    </span>
  );
}
